import React, { useEffect, useRef, useState, useContext } from "react";
import { getGeneralData, getBankBalance, getShareHolders, createExpanse, postGeneralData } from "../../../../Utility/Api";
import "bootstrap/dist/css/bootstrap.min.css";
import { toast } from "react-toastify";
import { currentMonth, NaviGate, shop_id, getStoredEntriesPerPage, getFormattedCurrentDate2 } from "../../../../Utility/function";
import { Paginations } from "../../../Common/Functions";
import { Wallet, ArrowDownLeft, ArrowUpRight, Printer, CheckCircle, Calendar } from "lucide-react";
import { WarehouseContext } from "../../../../Utility/UserContext";
import moment from "moment";

const FormattedDateInput = ({ id, label, value, onChange, min, max }) => {
  return (
    <div className="form-floating position-relative">
      <input
        type="date"
        id={id}
        value={value || ''}
        min={min}
        max={max}
        onChange={onChange}
        style={{
          opacity: 0,
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          zIndex: 10
        }}
      />
      <div
        className="form-control d-flex align-items-center justify-content-between"
        style={{
          paddingTop: '1.625rem',
          paddingBottom: '0.625rem',
          height: '58px',
          pointerEvents: 'none',
          backgroundColor: '#fff'
        }}
      >
        <span className={value ? "text-dark fw-medium" : "text-muted"}>
          {value ? moment(value).format('DD/MM/YYYY') : 'DD/MM/YYYY'}
        </span>
        <Calendar size={16} className="text-secondary ms-2 shrink-0" />
      </div>
      <label htmlFor={id} style={{ zIndex: 5 }}>{label}</label>
    </div>
  );
};

const BankLedger = () => {
  const contentRef = useRef(null);
  const { shopItem } = useContext(WarehouseContext);

  const todayStr = new Date().toISOString().split('T')[0];
  const firstDayStr = `${currentMonth()}-01`;

  const [filterType, setFilterType] = useState("custom"); // daily, weekly, monthly, yearly, custom
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [selectedMonth, setSelectedMonth] = useState(currentMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [customFromDate, setCustomFromDate] = useState(firstDayStr);
  const [customToDate, setCustomToDate] = useState(todayStr);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [allTransactions, setAllTransactions] = useState([]);
  const [exactBankBalance, setExactBankBalance] = useState(0);
  const [openingBalance, setOpeningBalance] = useState(null);

  // Withdraw Modal States
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [shareholders, setShareholders] = useState([]);
  const [withdrawData, setWithdrawData] = useState({
    amount: "",
    shareholder_id: "",
    remark: "",
    date: todayStr
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [companyInfo, setCompanyInfo] = useState({
    name: "Company Name",
    address: "123 Business Street, City, State 12345",
    phone: "(123) 456-7890",
    email: "info@yourcompany.com"
  });

  useEffect(() => {
    if (shopItem) {
      setCompanyInfo({
        name: shopItem?.title || "",
        address: shopItem?.Address || "123 Business Street, City, State 12345",
        phone: shopItem?.phone1 || "",
        email: shopItem?.email || ""
      });
    }
  }, [shopItem]);

  // Helper function to get current week range
  function getCurrentWeek() {
    const today = new Date();
    const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
    const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));

    return {
      start: firstDay.toISOString().split('T')[0],
      end: lastDay.toISOString().split('T')[0]
    };
  }

  // Helper function to get week range from date
  function getWeekRange(dateString) {
    const date = new Date(dateString);
    const firstDay = new Date(date.setDate(date.getDate() - date.getDay()));
    const lastDay = new Date(date.setDate(date.getDate() - date.getDay() + 6));

    return {
      start: firstDay.toISOString().split('T')[0],
      end: lastDay.toISOString().split('T')[0]
    };
  }

  useEffect(() => {
    updateDateRange();
  }, [filterType, selectedDate, selectedWeek, selectedMonth, selectedYear, customFromDate, customToDate]);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchTransactionData();
      fetchRealBankBalance();
    }
  }, [fromDate, toDate]);

  const fetchRealBankBalance = async () => {
    try {
      const res = await getBankBalance(shop_id);
      if (res.data?.status === "success") {
        setExactBankBalance(res.data.bank_balance);
      }
    } catch (err) {
      console.log("Error fetching bank balance:", err);
    }
  };

  const updateDateRange = () => {
    let startDate = "";
    let endDate = "";

    switch (filterType) {
      case "daily":
        startDate = selectedDate;
        endDate = selectedDate;
        break;
      case "weekly":
        startDate = selectedWeek.start;
        endDate = selectedWeek.end;
        break;
      case "monthly":
        startDate = `${selectedMonth}-01`;
        const lastDay = new Date(
          new Date(startDate).getFullYear(),
          new Date(startDate).getMonth() + 1,
          0
        ).getDate();
        endDate = `${selectedMonth}-${lastDay}`;
        break;
      case "yearly":
        startDate = `${selectedYear}-01-01`;
        endDate = `${selectedYear}-12-31`;
        break;
      case "custom":
        startDate = customFromDate;
        endDate = customToDate;
        break;
      default:
        break;
    }

    setFromDate(startDate);
    setToDate(endDate);
  };

  const fetchTransactionData = async () => {
    try {
      var filter = `start_date=${fromDate}&end_date=${toDate}&shop_id=${shop_id}`;
      const res = await getGeneralData(filter);
      if (res.data && res.data.data) {
        setAllTransactions(res.data.data);
      }
      if (res.data?.opening_bank_balance !== undefined) {
        setOpeningBalance(res.data.opening_bank_balance);
      } else {
        setOpeningBalance(null);
      }
    } catch (err) {
      console.log("Transaction Error", err);
    }
  };

  useEffect(() => {
    fetchShareholders();
  }, []);

  const fetchShareholders = async () => {
    try {
      const res = await getShareHolders(`shop_id=${shop_id}`);
      if (res.data?.data) {
        setShareholders(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching shareholders:", error);
    }
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    if (!withdrawData.shareholder_id || !withdrawData.amount || withdrawData.amount <= 0) {
      toast.error("Please enter a valid amount and select a shareholder");
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedSH = shareholders.find((s) => s.id == withdrawData.shareholder_id);
      const title = `Withdrawal - ${selectedSH ? selectedSH.name : "Shareholder"}`;

      const payload = new FormData();
      payload.append("title", title);
      payload.append("date", withdrawData.date);
      payload.append("amount", withdrawData.amount);
      payload.append("remark", withdrawData.remark);
      payload.append("type", "2"); // Other Expenses
      payload.append("in_or_out", "1"); // Expense Outflow
      payload.append("shop_id", shop_id);

      const res = await createExpanse(payload);
      if (res) {
        const createdId = res?.data?.data?.id || res?.data?.id || res?.id || "";

        // 1. Debit General Expenses (type "6")
        await postGeneralData({
          type: "6",
          ref_id: createdId,
          remarks: "Expense - " + title,
          date: withdrawData.date,
          created_by: shopItem?.user_id || "1",
          shop_id: shop_id,
          amount: withdrawData.amount,
          is_debit: "1", // Debit
        });

        // 2. Credit Bank/Cash (type "2")
        await postGeneralData({
          type: "2",
          ref_id: createdId,
          remarks: "Bank Entry - " + title,
          date: withdrawData.date,
          created_by: shopItem?.user_id || "1",
          shop_id: shop_id,
          amount: withdrawData.amount,
          is_debit: "0", // Credit (Outflow)
          name: "Bank",
        });

        toast.success("Withdrawal recorded successfully!");
        setShowWithdrawModal(false);
        setWithdrawData({ amount: "", shareholder_id: "", remark: "", date: todayStr });
        fetchTransactionData();
        fetchRealBankBalance();
      }
    } catch (err) {
      toast.error("Error recording withdrawal");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert("Please allow popups to print statements.");
      return;
    }

    const tableRows = bankTransactions.map((item, index) => `
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 12px; text-align: left; color: #475569;">${index + 1}</td>
        <td style="padding: 12px; text-align: left; color: #475569;">${item.date}</td>
        <td style="padding: 12px; text-align: left; font-weight: 500; color: #1e293b;">${item.ref_id || "-"}</td>
        <td style="padding: 12px; text-align: left; color: #475569;">${item.remarks || "-"}</td>
        <td style="padding: 12px; text-align: right; color: #10b981; font-weight: bold;">
          ${item.debit > 0 ? `₹${item.debit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}
        </td>
        <td style="padding: 12px; text-align: right; color: #ef4444; font-weight: bold;">
          ${item.credit > 0 ? `₹${item.credit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}
        </td>
      </tr>
    `).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bank Statement - ${companyInfo.name}</title>
        <style>
          @media print {
            @page {
              size: A4 portrait;
              margin: 15mm;
            }
          }
          body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            color: #1e293b;
            background: #fff;
            margin: 0;
            padding: 20px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header {
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header-title {
            font-size: 24px;
            font-weight: bold;
            color: #0f172a;
            margin: 0;
          }
          .company-name {
            font-size: 18px;
            font-weight: 600;
            color: #2563eb;
            margin: 5px 0 0 0;
          }
          .company-details {
            font-size: 12px;
            color: #64748b;
            margin: 3px 0;
          }
          .statement-info {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            margin-bottom: 25px;
            background-color: #f8fafc;
            padding: 12px;
            border-radius: 8px;
            border: 1px solid #f1f5f9;
          }
          .summary-cards {
            display: flex;
            gap: 15px;
            margin-bottom: 25px;
          }
          .card {
            flex: 1;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 12px;
            background-color: #fff;
          }
          .card-label {
            font-size: 11px;
            text-transform: uppercase;
            font-weight: 600;
            color: #64748b;
            margin-bottom: 5px;
          }
          .card-value {
            font-size: 16px;
            font-weight: 700;
          }
          .table-container {
            width: 100%;
            margin-bottom: 30px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }
          th {
            background-color: #f1f5f9;
            color: #475569;
            font-weight: 600;
            padding: 12px;
            text-align: left;
            border-bottom: 2px solid #cbd5e1;
          }
          td {
            padding: 12px;
            border-bottom: 1px solid #e2e8f0;
          }
          .text-right {
            text-align: right;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 11px;
            color: #94a3b8;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div style="display: flex; justify-content: space-between; align-items: flex-start;">
            <div>
              <h1 class="header-title">Bank Account Statement</h1>
              <p class="company-name">${companyInfo.name}</p>
              <p class="company-details">${companyInfo.address}</p>
              <p class="company-details">Phone: ${companyInfo.phone} | Email: ${companyInfo.email}</p>
            </div>
            <div style="text-align: right; font-size: 12px; color: #64748b;">
              <p style="margin: 0;"><strong>Statement ID:</strong> ST-${Date.now().toString().slice(-6)}</p>
              <p style="margin: 5px 0 0 0;"><strong>Date range:</strong> ${fromDate} to ${toDate}</p>
            </div>
          </div>
        </div>

        <div class="statement-info">
          <div>
            <strong>Report Type:</strong> Bank Ledger Summary
          </div>
          <div>
            <strong>Generated On:</strong> ${new Date().toLocaleDateString('en-IN')} ${new Date().toLocaleTimeString('en-IN')}
          </div>
        </div>

        <div class="summary-cards">
          <div class="card" style="border-left: 4px solid #94a3b8;">
            <div class="card-label">Opening Balance</div>
            <div class="card-value" style="color: #475569;">${openingBalance !== null ? `₹ ${openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</div>
          </div>
          <div class="card" style="border-left: 4px solid #10b981;">
            <div class="card-label">Net Ledger Balance (Live)</div>
            <div class="card-value" style="color: #059669;">₹ ${exactBankBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
        <div class="summary-cards">
          <div class="card" style="border-left: 4px solid #3b82f6;">
            <div class="card-label">Total Inflows (Added)</div>
            <div class="card-value" style="color: #1d4ed8;">₹ ${totalAdded.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div class="card" style="border-left: 4px solid #ef4444;">
            <div class="card-label">Total Outflows (Deducted)</div>
            <div class="card-value" style="color: #b91c1c;">₹ ${totalDeducted.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th style="width: 5%">S.No</th>
                <th style="width: 15%">Date</th>
                <th style="width: 25%">Reference ID</th>
                <th style="width: 25%">Remarks</th>
                <th style="width: 15%; text-align: right;">Added (Debit)</th>
                <th style="width: 15%; text-align: right;">Deducted (Credit)</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows.length > 0 ? tableRows : `<tr><td colspan="6" style="text-align: center; padding: 20px; color: #94a3b8;">No bank transactions recorded for this period.</td></tr>`}
            </tbody>
          </table>
        </div>

        <div class="footer">
          Thank you for choosing ${companyInfo.name}. This is a computer-generated bank ledger statement.
        </div>

        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 100);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleWeekChange = (dateString) => {
    setSelectedWeek(getWeekRange(dateString));
  };

  const renderFilterControls = () => {
    switch (filterType) {
      case "daily":
        return (
          <div className="col-auto">
            <FormattedDateInput
              id="selectedDate"
              label="Select Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        );
      case "weekly":
        return (
          <div className="col-auto">
            <FormattedDateInput
              id="weekDate"
              label="Select Week"
              value={selectedWeek.start}
              onChange={(e) => handleWeekChange(e.target.value)}
            />
          </div>
        );
      case "monthly":
        return (
          <div className="col-auto">
            <div className="form-floating">
              <input
                type="month"
                className="form-control"
                id="selectedMonth"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
              <label htmlFor="selectedMonth">Select Month</label>
            </div>
          </div>
        );
      case "yearly":
        return (
          <div className="col-auto">
            <div className="form-floating">
              <input
                type="number"
                className="form-control"
                id="selectedYear"
                min="2000"
                max="2100"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              />
              <label htmlFor="selectedYear">Select Year</label>
            </div>
          </div>
        );
      case "custom":
        return (
          <>
            <div className="col-auto">
              <FormattedDateInput
                id="customFromDate"
                label="From Date"
                value={customFromDate}
                max={customToDate}
                onChange={(e) => setCustomFromDate(e.target.value)}
              />
            </div>
            <div className="col-auto">
              <FormattedDateInput
                id="customToDate"
                label="To Date"
                value={customToDate}
                min={customFromDate}
                onChange={(e) => setCustomToDate(e.target.value)}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  // Filter Bank entries (type === "2")
  const bankTransactions = allTransactions
    .filter((item) => item.type === "2" && item.amount)
    .map((item) => ({
      date: item.date,
      ref_id: item.ref_id,
      remarks: item.remarks,
      debit: item.is_debit == "1" ? parseFloat(item.amount) : 0,
      credit: item.is_debit == "0" ? parseFloat(item.amount) : 0,
    }));

  const totalAdded = bankTransactions.reduce((sum, item) => sum + item.debit, 0);
  const totalDeducted = bankTransactions.reduce((sum, item) => sum + item.credit, 0);
  const netPeriodBalance = totalAdded - totalDeducted;
  const closingBalance = openingBalance !== null ? openingBalance + netPeriodBalance : null;

  return (
    <div className="page-content">
      <NaviGate />
      <div className="card p-3 mx-2 overflow-y-scroll" style={{ minHeight: "90vh" }} ref={contentRef}>
        {/* <div className="row mb-4 border-bottom justify-content-between align-items-center w-100 pb-3"> */}
        <div className="row mx-0 mb-4 border-bottom justify-content-between align-items-center pb-3">
          <h5 className="text-start w-auto fw-bold mb-0 text-primary">
            Bank Management
          </h5>
          <div className="w-auto d-flex gap-2 mt-0 justify-content-end align-items-center">
            {/* 
            <div className="col-auto">
              <div className="form-floating">
                <select
                  className="form-select"
                  id="filterType"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{ height: "50px", paddingTop: "15px", minWidth: "130px" }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="custom">Custom Range</option>
                </select>
                <label htmlFor="filterType">Filter By</label>
              </div>
            </div>
            */}

            {renderFilterControls()}

            <button
              type="button"
              className="btn btn-primary fw-bold d-flex align-items-center gap-2"
              onClick={() => setShowWithdrawModal(true)}
              style={{ height: "58px", backgroundColor: "#3b82f6", borderColor: "#3b82f6" }}
            >
              <ArrowUpRight size={18} />
              Withdraw
            </button>

            <button
              type="button"
              className="btn btn-outline-primary fw-bold d-flex align-items-center gap-2"
              onClick={handlePrint}
              style={{ height: "58px" }}
            >
              <Printer size={18} />
              Print
            </button>
          </div>
        </div>

        {/* Balance Dashboard Cards */}
        <div className="row mb-4 g-3 align-items-stretch">
          <div className="col-md-3">
            <div className="card h-100 border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #64748b, #475569)", borderRadius: "12px" }}>
              <div className="card-body p-3 d-flex justify-content-between align-items-center">
                <div className="me-2" style={{ minWidth: 0 }}>
                  <p className="mb-1 text-white-50 text-uppercase fw-semibold" style={{ fontSize: "11px" }}>Period Opening Balance</p>
                  <h4 className="mb-0 fw-bold text-nowrap" style={{ fontSize: "1.3rem" }}>{openingBalance !== null ? `₹ ${openingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</h4>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ backgroundColor: "rgba(255, 255, 255, 0.2)", width: "45px", height: "45px" }}>
                  <Calendar className="text-white" size={22} />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card h-100 border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #10b981, #059669)", borderRadius: "12px" }}>
              <div className="card-body p-3 d-flex justify-content-between align-items-center">
                <div className="me-2" style={{ minWidth: 0 }}>
                  <p className="mb-1 text-white-50 text-uppercase fw-semibold" style={{ fontSize: "11px" }}>Current Live Balance</p>
                  <h4 className="mb-0 fw-bold text-nowrap" style={{ fontSize: "1.3rem" }}>₹ {exactBankBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ backgroundColor: "rgba(255, 255, 255, 0.2)", width: "45px", height: "45px" }}>
                  <Wallet className="text-white" size={22} />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card h-100 border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #3b82f6, #1d4ed8)", borderRadius: "12px" }}>
              <div className="card-body p-3 d-flex justify-content-between align-items-center">
                <div className="me-2" style={{ minWidth: 0 }}>
                  <p className="mb-1 text-white-50 text-uppercase fw-semibold" style={{ fontSize: "11px" }}>Total Period Inflows</p>
                  <h4 className="mb-0 fw-bold text-nowrap" style={{ fontSize: "1.3rem" }}>₹ {totalAdded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ backgroundColor: "rgba(255, 255, 255, 0.2)", width: "45px", height: "45px" }}>
                  <ArrowDownLeft className="text-white" size={22} />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card h-100 border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #ef4444, #b91c1c)", borderRadius: "12px" }}>
              <div className="card-body p-3 d-flex justify-content-between align-items-center">
                <div className="me-2" style={{ minWidth: 0 }}>
                  <p className="mb-1 text-white-50 text-uppercase fw-semibold" style={{ fontSize: "11px" }}>Total Period Outflows</p>
                  <h4 className="mb-0 fw-bold text-nowrap" style={{ fontSize: "1.3rem" }}>₹ {totalDeducted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ backgroundColor: "rgba(255, 255, 255, 0.2)", width: "45px", height: "45px" }}>
                  <ArrowUpRight className="text-white" size={22} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Closing Balance Alert */}
        {closingBalance !== null && (
          <div className="alert alert-secondary d-flex align-items-center mb-4" style={{ backgroundColor: "#f8fafc", borderColor: "#e2e8f0" }}>
            <CheckCircle className="text-success me-2" size={20} />
            <strong className="me-2 text-dark">Period Closing Balance:</strong>
            <span className="text-primary fw-bold fs-5">₹ {closingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <span className="ms-auto text-muted fs-6">
              (Calculated as: Opening Balance + Total Inflows - Total Outflows)
            </span>
          </div>
        )}

        <div className="d-flex justify-content-between mb-3 text-secondary">
          <div>
            <strong>Statement Period:</strong> {fromDate} to {toDate}
          </div>
        </div>

        <div className="row g-4 pt-0">
          <div className="col-md-12 mt-2">
            <BankTransactionTable
              transactions={bankTransactions}
            />
          </div>
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-light">
                <h5 className="modal-title text-primary fw-bold">
                  <ArrowUpRight className="me-2 text-danger" />
                  Withdraw Funds
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowWithdrawModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleWithdrawSubmit}>

                  <div className="mb-3">
                    <label className="form-label text-muted fw-bold">Current Bank Balance</label>
                    <div className="fs-4 fw-bold text-success">
                      ₹ {exactBankBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Date</label>
                    <div className="position-relative">
                      <input
                        type="date"
                        value={withdrawData.date}
                        max={todayStr}
                        onChange={(e) => setWithdrawData({ ...withdrawData, date: e.target.value })}
                        style={{ opacity: 0, position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'pointer', zIndex: 10 }}
                        required
                      />
                      <div className="form-control d-flex align-items-center justify-content-between" style={{ pointerEvents: 'none' }}>
                        <span className={withdrawData.date ? "text-dark" : "text-muted"}>
                          {withdrawData.date ? moment(withdrawData.date).format('DD/MM/YYYY') : 'DD/MM/YYYY'}
                        </span>
                        <Calendar size={16} className="text-secondary" />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Shareholder Name</label>
                    <select
                      className="form-select"
                      value={withdrawData.shareholder_id}
                      onChange={(e) => setWithdrawData({ ...withdrawData, shareholder_id: e.target.value })}
                      required
                    >
                      <option value="">-- Select Shareholder --</option>
                      {shareholders.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Amount (INR)</label>
                    <div className="input-group">
                      <span className="input-group-text">₹</span>
                      <input
                        type="number"
                        className="form-control fw-bold"
                        placeholder="Enter amount"
                        value={withdrawData.amount}
                        onChange={(e) => setWithdrawData({ ...withdrawData, amount: e.target.value })}
                        min="1"
                        max={exactBankBalance}
                        required
                      />
                    </div>
                    {withdrawData.amount > exactBankBalance && (
                      <small className="text-danger fw-bold mt-1 d-block">Amount exceeds current bank balance!</small>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">Remarks</label>
                    <textarea
                      className="form-control"
                      placeholder="e.g. Dividend payment for Q3"
                      value={withdrawData.remark}
                      onChange={(e) => setWithdrawData({ ...withdrawData, remark: e.target.value })}
                      rows="2"
                    ></textarea>
                  </div>

                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary fw-bold p-3 fs-5"
                      disabled={isSubmitting || !withdrawData.amount || withdrawData.amount > exactBankBalance || withdrawData.amount <= 0}
                    >
                      {isSubmitting ? "Processing..." : "Confirm Withdrawal"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowWithdrawModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const BankTransactionTable = ({ transactions }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(getStoredEntriesPerPage(10));

  const totalPages = Math.ceil(transactions.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = transactions.slice(startIndex, startIndex + rowsPerPage);

  return (
    <>
      <div className="p-0 bg-white">
        <div className="w-full">
          <div className="table-responsive">
            {/* <table className="table table-hover table-bordered mb-0 align-middle"> */}
            <table className="table table-hover table-bordered mb-0 align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
              <thead className="table-light">
                <tr>
                  {/* <th className="py-3 px-4 text-start" style={{ width: "15%" }}>Date</th>
                  <th className="py-3 px-4 text-start" style={{ width: "25%" }}>Reference ID</th>
                  <th className="py-3 px-4 text-start" style={{ width: "30%" }}>Remarks</th>
                  <th className="py-3 px-4 text-end text-success" style={{ width: "15%" }}>Added (Debit) (INR)</th>
                  <th className="py-3 px-4 text-end text-danger" style={{ width: "15%" }}>Deducted (Credit) (INR)</th> */}
                  <th className="py-3 px-4 text-center" style={{ width: "15%", whiteSpace: "nowrap" }}>Date</th>
                  <th className="py-3 px-4 text-center" style={{ width: "20%", whiteSpace: "normal", wordBreak: "break-word" }}>Reference ID</th>
                  <th className="py-3 px-4 text-center" style={{ width: "35%", whiteSpace: "normal", wordBreak: "break-word" }}>Remarks</th>
                  <th className="py-3 px-4 text-center text-success" style={{ width: "15%", whiteSpace: "normal", wordBreak: "break-word" }}>Added (Debit) (INR)</th>
                  <th className="py-3 px-4 text-center text-danger" style={{ width: "15%", whiteSpace: "normal", wordBreak: "break-word" }}>Deducted (Credit) (INR)</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <tr key={index} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      {/* <td className="py-3 px-4 text-secondary">{item.date}</td>
                      <td className="py-3 px-4 fw-semibold text-dark">{item.ref_id || "-"}</td>
                      <td className="py-3 px-4 text-secondary">{item.remarks}</td>
                      <td className="py-3 px-4 text-end text-success fw-bold"></td> */}
                      <td className="py-3 px-4 text-secondary text-center" style={{ whiteSpace: "nowrap" }}>{item.date}</td>
                      <td className="py-3 px-4 fw-semibold text-dark" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>{item.ref_id || "-"}</td>
                      <td className="py-3 px-4 text-secondary" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>{item.remarks}</td>
                      <td className="py-3 px-4 text-end text-success fw-bold" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                        {item.debit > 0 ? `₹ ${item.debit.toFixed(2)}` : "-"}
                      </td>
                      {/* <td className="py-3 px-4 text-end text-danger fw-bold"> */}
                      <td className="py-3 px-4 text-end text-danger fw-bold" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                        {item.credit > 0 ? `₹ ${item.credit.toFixed(2)}` : "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      No bank/cash transactions recorded for this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {transactions.length > 0 && (
            <div className="mt-3">
              <Paginations
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                entriesPerPage={rowsPerPage}
                onEntriesPerPageChange={(val) => {
                  setRowsPerPage(val);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BankLedger;
